import { useIntl } from "react-intl";

import { IQuotation } from "pages/shipper/ShipmentQuotes/components/quotes/QuotesType";
import { Row } from "react-bootstrap";
import { formatWeight } from "helpers/commonHelpers";
import { useGetCostDetails } from "api/queries/quotation/useGetCostDetails";
import Divider from "components/v2/Divider/Divider";
import { FreightModeBaseTypes } from "../../../../../../../constants";
import QuotationDetailServiceConfirmationItem from "./QuotationDetailServiceConfirmationItem";

type QuotationDetailServiceConfirmationProps = {
  currentQuotation: IQuotation;
};

function QuotationDetailServiceConfirmation(props: QuotationDetailServiceConfirmationProps) {
  const { currentQuotation } = props;
  const intl = useIntl();

  const { data: costDetailsData } = useGetCostDetails(currentQuotation.reference, {
    enabled: !!currentQuotation.reference,
  });

  if (
    !currentQuotation?.service?.service_confirmation_data?.length &&
    (!costDetailsData?.service_level ||
      !Object.keys(costDetailsData?.service_level)?.length ||
      !Object.values(costDetailsData?.service_level)?.some((item) => !!item))
  ) {
    return null;
  }

  return (
    <>
      <div className="px-3xl">
        <div className="text-lg font-semibold text-primary mb-xl">Service confirmation</div>
        {currentQuotation?.service?.service_confirmation_data?.length > 0 && (
          <Row>
            {currentQuotation?.service?.service_confirmation_data?.map((item) => {
              const {
                is_available,
                question_response,
                service_question,
                service_type,
                why_not_available,
                selected_incoterm,
              } = item;
              let label;
              if (service_type === "Multiple choice" || service_question !== "") label = service_question;
              else label = service_type;

              let value;
              if (["FREE_TEXT", "MULTIPLE"].includes(service_type)) value = question_response;
              else if (service_type === "YESNO") value = is_available;
              else if (service_question !== "") value = question_response;
              else value = is_available;

              const additionalValue =
                why_not_available && selected_incoterm
                  ? `${why_not_available}, ${selected_incoterm}`
                  : why_not_available || selected_incoterm;

              return (
                <QuotationDetailServiceConfirmationItem
                  key={label}
                  label={label}
                  value={value}
                  additionalValue={additionalValue}
                />
              );
            })}
          </Row>
        )}
        {currentQuotation?.service?.freight_mode_type === FreightModeBaseTypes.AIR && (
          <Row>
            {costDetailsData?.service_level?.details?.max_allowed_wt && (
              <QuotationDetailServiceConfirmationItem
                label={intl.formatMessage({
                  id: "quote-request.costDetailOBCMaxAllowWeight",
                  defaultMessage: "Max allowed weight",
                })}
                value={costDetailsData.service_level.details.max_allowed_wt}
              />
            )}
            {costDetailsData?.service_level?.details?.total_flight_duration && (
              <QuotationDetailServiceConfirmationItem
                label={intl.formatMessage({
                  id: "quote-request.costDetailAirCarterTotalFlightDuration",
                  defaultMessage: "Total flight duration",
                })}
                value={costDetailsData.service_level.details.total_flight_duration}
              />
            )}
            {costDetailsData?.service_level?.details?.ptc && (
              <QuotationDetailServiceConfirmationItem
                label={intl.formatMessage({
                  id: "quote-request.costDetailAirCarterPTC",
                  defaultMessage: "Positioning time from confirmation",
                })}
                value={costDetailsData.service_level.details.ptc}
              />
            )}
            {costDetailsData?.service_level?.details?.aircraft_type && (
              <QuotationDetailServiceConfirmationItem
                label={intl.formatMessage({
                  id: "quote-request.costDetailAirCarterAircraftType",
                  defaultMessage: "Aircraft type",
                })}
                value={costDetailsData.service_level.details.aircraft_type}
              />
            )}
            {costDetailsData?.service_level?.details?.maximum_payload && (
              <QuotationDetailServiceConfirmationItem
                label={intl.formatMessage({
                  id: "quote-request.costDetailAirCarterMaximumPayload",
                  defaultMessage: "Maximum payload",
                })}
                value={costDetailsData.service_level.details.maximum_payload}
              />
            )}
            {costDetailsData?.service_level?.details?.chargeable_wt > 0 && (
              <QuotationDetailServiceConfirmationItem
                label={intl.formatMessage({
                  id: "quote-request.costDetailChargeableWeight",
                  defaultMessage: "Chargeable weight",
                })}
                value={formatWeight(
                  costDetailsData?.service_level?.details?.chargeable_wt,
                  costDetailsData?.service_level?.details?.chargeable_wt_unit,
                )}
              />
            )}
          </Row>
        )}
        {currentQuotation?.service?.freight_mode_type === FreightModeBaseTypes.ROAD && (
          <Row>
            {costDetailsData?.service_level?.details?.max_wt > 0 && (
              <QuotationDetailServiceConfirmationItem
                label={intl.formatMessage({
                  id: "quote-request.costDetailChargeableWeight",
                  defaultMessage: "Chargeable weight",
                })}
                value={formatWeight(
                  costDetailsData.service_level.details.max_wt,
                  costDetailsData?.service_level?.details?.max_wt_unit,
                )}
              />
            )}
            {costDetailsData?.service_level?.details?.max_dimension > 0 && (
              <QuotationDetailServiceConfirmationItem
                label={intl.formatMessage({
                  id: "quote-request.costDetailRoadFreightMaximumDimension",
                  defaultMessage: "Maximum Dimensions",
                })}
                value={costDetailsData.service_level.details.max_dimension}
              />
            )}
          </Row>
        )}
      </div>
      <Divider className="my-xl" />
    </>
  );
}

export default QuotationDetailServiceConfirmation;
